import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {number | undefined} */
  #highlightTimeout;

  /** @type {MutationObserver | null} */
  #packPickerAtcObserver = null;

  /** @type {boolean} */
  #showingIncompleteError = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // Ray Pack Picker never dispatches events (see assets/ray-pack-picker.js) - clicks/changes
    // catch the shopper's own selections, but its add-to-cart button also updates its own label
    // asynchronously afterwards ("Додається..." -> "Додано!" -> back to normal, on a setTimeout,
    // not a click) - only a MutationObserver on it catches that part.
    const picker = this.#getRayPackPicker();
    if (picker) {
      document.addEventListener('click', this.#handlePackPickerInteraction, { signal });
      document.addEventListener('change', this.#handlePackPickerInteraction, { signal });

      const atcBtn = picker.querySelector('#rpp-atc-btn');
      if (atcBtn) {
        const observer = new MutationObserver(() => this.#syncFromPackPicker());
        observer.observe(atcBtn, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
        this.#packPickerAtcObserver = observer;
      }

      this.#syncFromPackPicker();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#packPickerAtcObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#highlightTimeout) {
      clearTimeout(this.#highlightTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    // Ray Pack Picker force-hides product-form-component (html.ray-pack-active rules in
    // assets/ray-pack-picker.css), collapsing .buy-buttons-block to a zero-size box that never
    // satisfies the observer below - watch the picker itself instead on those products.
    const buyButtonsBlock = this.#getRayPackPicker() || productForm?.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const picker = this.#getRayPackPicker();
    const incomplete = picker && this.#getPackPickerIncompleteState(picker);

    if (incomplete) {
      this.#showingIncompleteError = true;
      this.#showPackPickerSectionError(incomplete);
      this.#highlightPackPickerSection(incomplete.section);
      return;
    }

    const targetButton = this.#getEffectiveAddToCartButton();
    if (!targetButton) return;
    targetButton.dataset.puppet = 'true';
    targetButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    const targetButton = this.#getEffectiveAddToCartButton();
    if (targetButton) targetButton.dataset.puppet = 'false';
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getRayPackPicker() {
    return document.getElementById('ray-pack-picker');
  }

  /**
   * The real add-to-cart button behind this sticky bar's proxy click - Ray Pack Picker's own
   * #rpp-atc-btn when present (the stock button stays hidden and would add the wrong variant),
   * otherwise the stock product form's button.
   * @returns {HTMLButtonElement | null}
   */
  #getEffectiveAddToCartButton() {
    const picker = this.#getRayPackPicker();
    const packAtcBtn = /** @type {HTMLButtonElement | null} */ (picker?.querySelector('#rpp-atc-btn'));
    return packAtcBtn ?? this.#targetAddToCartButton;
  }

  /**
   * @param {Element} picker
   */
  #getPackPickerSelectedOptions(picker) {
    const color = /** @type {HTMLElement | null} */ (picker.querySelector('.rpp-swatch.is-active'))?.dataset.color;
    const size = /** @type {HTMLElement | null} */ (picker.querySelector('.rpp-size-btn.is-active'))?.dataset.size;
    const format = /** @type {HTMLElement | null} */ (picker.querySelector('.rpp-format-tab.is-active'))?.dataset
      .format;
    /** @type {Record<string, string>} */
    const selectedOptions = {};

    if (color) selectedOptions.option1 = color;
    if (size) selectedOptions.option2 = size;
    if (format) selectedOptions.option3 = format;

    return selectedOptions;
  }

  #getPackPickerVariants() {
    const json = document.getElementById('ray-pack-variants-data');
    if (!json?.textContent) return [];

    try {
      return JSON.parse(json.textContent.trim());
    } catch {
      return [];
    }
  }

  /**
   * @param {number} cents
   * @param {string | undefined} format
   */
  #applyMoneyFormat(cents, format) {
    const amount = (cents / 100).toFixed(2);

    if (!format) return amount;

    const noDecimal = Math.round(cents / 100).toString();
    const withComma = amount.replace('.', ',');

    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', noDecimal)
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimal);
  }

  #handlePackPickerInteraction = (/** @type {Event} */ event) => {
    const picker = this.#getRayPackPicker();
    if (!picker || !(event.target instanceof Node) || !picker.contains(event.target)) return;

    this.#syncFromPackPicker();
  };

  /**
   * Mirrors Ray Pack Picker's own #rpp-atc-btn readiness into this bar's button (label stays a
   * static "Додати в корзину" - see handleAddToCartClick for the guide-to-missing-option
   * behavior instead), and resolves the currently active variant from the page's existing
   * #ray-pack-variants-data JSON so the price/thumbnail stay in sync (Ray Pack Picker never
   * touches the native variant input/events).
   * @returns {boolean} whether a pack picker was found and synced
   */
  #syncFromPackPicker() {
    const picker = this.#getRayPackPicker();
    if (!picker) return false;

    const needSize = !picker.querySelector('.rpp-size-btn.is-active');
    const { addToCartButton, quantityDisplay } = this.refs;
    const incomplete = this.#getPackPickerIncompleteState(picker);

    // The button's own label always stays "Додати в корзину" - clicking while incomplete
    // guides the shopper to the missing option instead of relabelling the button itself.
    addToCartButton.disabled = incomplete ? false : !!picker.querySelector('#rpp-atc-btn')?.disabled;
    quantityDisplay.style.display = 'none';

    if (this.#showingIncompleteError) {
      this.#showPackPickerSectionError(incomplete);
      this.#showingIncompleteError = !!incomplete;
    }

    if (needSize) {
      // Nothing is actually selected yet - the server-rendered default variant title (e.g.
      // "Black / XS / 1 шт") would misleadingly look like a real selection here.
      const variantEl = this.querySelector('.sticky-add-to-cart__variant');
      if (variantEl) variantEl.textContent = '';
      return true;
    }

    const selectedOptions = this.#getPackPickerSelectedOptions(picker);
    const variants = this.#getPackPickerVariants();
    const matches = (/** @type {any} */ variant) =>
      Object.entries(selectedOptions).every(([key, value]) => !value || variant[key] === value);
    const variant = variants.find((/** @type {any} */ v) => v.available && matches(v)) || variants.find(matches);

    if (!variant) return true;

    const priceEl = this.querySelector('.sticky-add-to-cart__price');
    if (priceEl) {
      // variant.price here is the raw variant price - it doesn't include the pack/"набір"
      // discount, which assets/ray-pack-picker.js computes client-side and writes only into
      // the main product-price element. Mirror that already-computed text instead of
      // re-deriving the discount here, so the two can't drift apart.
      const packDisplayedPrice = this.#getPackPickerDisplayedPriceText();
      priceEl.textContent = packDisplayedPrice || this.#applyMoneyFormat(variant.price, this.dataset.moneyFormat);
    }

    const variantEl = this.querySelector('.sticky-add-to-cart__variant');
    if (variantEl) variantEl.textContent = Object.values(selectedOptions).join(' / ');

    if (variant.featured_image && this.refs.productImage) {
      this.refs.productImage.src = variant.featured_image;
    }

    return true;
  }

  /**
   * Reads the already-computed price text back out of the main product-price element (the one
   * assets/ray-pack-picker.js itself writes the pack-discounted price into), rather than
   * recomputing the "набір" discount independently here.
   * @returns {string | null}
   */
  #getPackPickerDisplayedPriceText() {
    const productPrice = document.querySelector('product-price');
    const priceContainer = productPrice?.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return null;

    const activeWrap =
      priceContainer.querySelector('.price__sale:not(.price__hidden)') ||
      priceContainer.querySelector('.price__regular:not(.price__hidden)');
    const activePriceEl = activeWrap?.querySelector('.price-item--sale.price, .price');

    return activePriceEl?.textContent?.trim() || null;
  }

  /**
   * Reads Ray Pack Picker's own "what's missing" state (its #rpp-atc-btn/#rpp-atc-text) instead
   * of re-deriving it, so every selection mode (single variant, pack format, custom multi-color
   * bundle) stays in sync for free. Mirrors assets/ray-bundle-offer.js's identically-named
   * helper - the two can't share a module (this file is an ES module, that one a plain script -
   * see its own header comment), but keep the same target section ids / element attributes /
   * class names so both interoperate on the one shared #ray-pack-picker instance.
   * @param {Element} picker
   * @returns {{ section: Element, message: string } | null}
   */
  #getPackPickerIncompleteState(picker) {
    const atcBtn = /** @type {HTMLButtonElement | null} */ (picker.querySelector('#rpp-atc-btn'));
    const atcText = picker.querySelector('#rpp-atc-text');

    if (!atcBtn || !atcBtn.disabled) return null;

    const metaEl = document.getElementById('ray-pack-meta');
    let labels = {};

    if (metaEl) {
      try {
        labels = JSON.parse(metaEl.textContent?.trim() || '{}').labels || {};
      } catch {
        labels = {};
      }
    }

    const currentLabel = atcText ? atcText.textContent?.trim() || '' : '';

    let section = picker;

    if (labels.chooseSize && currentLabel === labels.chooseSize) {
      section = picker.querySelector('#rpp-size-section') || section;
    } else if (labels.choosePackButton && currentLabel === labels.choosePackButton) {
      section = picker.querySelector('#rpp-pack-cards') || section;
    } else if (labels.chooseAllColors && currentLabel === labels.chooseAllColors) {
      section = picker.querySelector('#rpp-custom-bundle') || section;
    }

    return { section, message: currentLabel };
  }

  /**
   * @param {{ section: Element, message: string } | null} incomplete
   */
  #showPackPickerSectionError(incomplete) {
    document.querySelectorAll('[data-rpp-section-error]').forEach((el) => {
      el.textContent = '';
      /** @type {HTMLElement} */ (el).hidden = true;
    });

    if (!incomplete) return;

    let el = incomplete.section.querySelector('[data-rpp-section-error]');

    if (!el) {
      el = document.createElement('p');
      el.className = 'rpp-section-error';
      el.setAttribute('data-rpp-section-error', '');
      incomplete.section.appendChild(el);
    }

    el.textContent = incomplete.message;
    /** @type {HTMLElement} */ (el).hidden = false;
  }

  /**
   * @param {Element | null | undefined} section
   */
  #highlightPackPickerSection(section) {
    if (!section) return;

    section.classList.remove('rpp-highlight');
    void /** @type {HTMLElement} */ (section).offsetWidth; // restart the animation if mid-run
    section.classList.add('rpp-highlight');
    section.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (this.#highlightTimeout) clearTimeout(this.#highlightTimeout);
    this.#highlightTimeout = setTimeout(() => {
      section.classList.remove('rpp-highlight');
    }, 1600);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
